import { axiosSecuredInstance } from "configurations/axiosConfig";
import { isValidDate } from "pages/file/utils/helper";
import { useRef } from "react";
import { useCompanyContext } from "utils/context/CompanyContext";
import { toSelectFieldOption, useEndorsementActions } from "utils/context/CompanyEndorsementContext";
import { useFiles } from "utils/context/FilesContext";
import { useGlobalAccess } from "utils/context/GlobalAccessContext";
import useFormWrapper, {
  getNameString,
} from "utils/custom-hooks/useFormWrapper";
import {
  ProductType,
  UIConstants,
} from "utils/data/enum";
import { addDays, formatDateToString } from "utils/shared";


const useEffectiveDateValidation = () => {
  const [, { getComapanyContractEffectiveDates }] = useCompanyContext();
  const [, { validateEndorsementEffectiveDate }] = useEndorsementActions();
  const { getValues, setValue, setError } = useFormWrapper();
  const [{ isReadOnly }] = useFiles();
  const [{ selectedAgency }] = useGlobalAccess();  
  const abortControllerRef = useRef<AbortController | null>(null);

  const validateEffectiveDate = async (
    product: string,
    index: number | undefined,
    newDate: Date | null | undefined,
    isCallingSetError: boolean = true,
    setEffectiveDateValidationMsg: boolean = true,
  ): Promise<string> => {
    let msg = "";
    if (!isReadOnly && newDate && isValidDate(newDate)) {
      newDate.setHours(0, 0, 0);
      const todayPlus30 = addDays(new Date(), 30);
      todayPlus30.setTime(todayPlus30.getTime() + todayPlus30.getTimezoneOffset() * 60000);
      todayPlus30.setHours(0, 0, 0);

      if (newDate > todayPlus30) {
        msg = UIConstants.EFFECTIVE_DATE_NOT_WITHIN_30_DAYS_ERROR_MESSAGE;
      }
      else {
        let productType = "";
        switch (product) {
          case "cpls":
            productType = ProductType.Cpl;
            break;
          case "aALProducts":
            productType = ProductType.Aal;
            break;
          case "jackets":
            productType = ProductType.Jacket;
            break;
          default:
            break;
        }

        let agencyId = getValues("agency.id");
        if(!agencyId) {          
          agencyId = selectedAgency ? selectedAgency[0]?.CompanyID : "";          
        }

        if(!agencyId || !productType) {
          return msg;
        }
        
        const comapanyContractEffectiveDates = await getComapanyContractEffectiveDates(
          agencyId,
          productType
        );
        
        if (comapanyContractEffectiveDates === undefined || !comapanyContractEffectiveDates?.some(cd => {
          let startDate: Date;
          let endDate: Date;
          if (cd.effectiveDate) {
            startDate = new Date(cd.effectiveDate);
            startDate.setTime(startDate.getTime() + startDate.getTimezoneOffset() * 60000);
            startDate.setHours(0, 0, 0);
          }
          else {
            startDate = new Date(0);
          }
          if (cd.effectiveEndDate) {
            endDate = new Date(cd.effectiveEndDate);
            endDate.setTime(endDate.getTime() + endDate.getTimezoneOffset() * 60000);
            endDate.setHours(23, 59, 59);
          }
          else {
            endDate = todayPlus30;
          }

          return newDate >= startDate && newDate <= endDate;
        })) {
          msg = UIConstants.EFFECTIVE_DATE_NOT_VALID_ERROR_MESSAGE;
        }
      }

      if (isCallingSetError && msg !== "") {
        setError(
          getNameString(`${product}.${index}.effectiveDate`),
          {
            type: "focus",
            message: msg,
          },
          { shouldFocus: false }
        );
      }

      if (setEffectiveDateValidationMsg) {
        setValue(`${product}.${index}.effectiveDateValidationMsg`, msg);
      }
    }
    return msg;
  };

  const validateEndorsementWithEffectiveDate = async (
    schema: string,
    companyId: string,
    stateAbbr: string,
    jacketFormType: string,
    endorsementId: string,
    effectiveDate: Date | null,
    formId?: string,
    // newEndorsementDDList?: SelectFieldOption[],
  ): Promise<string> => {
    let msg = "";
    if (!isReadOnly && jacketFormType && effectiveDate && isValidDate(effectiveDate)) {

      const result = await validateEndorsementEffectiveDate(companyId, jacketFormType, stateAbbr, endorsementId, effectiveDate, formId);

      if (!result || result.length === 0 || result[0].isValid === 0) {
        // set endorsement validation message
        msg = UIConstants.ENDORSEMENT_NOT_VALID_FOR_SELECTED_EFFECTIVE_DATE;
        setValue(`${schema}.effectiveDateValidationMsg`, msg);
        // set new endorsement DDL values 
        setValue(`${schema}.validEndorsementDDList`, toSelectFieldOption(result) || []);
      }
      else {
        msg = "";
        setValue(`${schema}.effectiveDateValidationMsg`, msg);
        // set new endorsement DDL values 
        setValue(`${schema}.validEndorsementDDList`, []);

      }

        
    }
    return msg;
  }

  // Validates that the product's selected agency location is still valid for a single
  // endorsement's effective date. Unlike validateLocationWithEffectiveDate below, this takes a
  // full schema path (e.g. standaloneEndorsements.0.endorsements.2) because the message is
  // reported on the endorsement row that owns the offending date.
  const validateEndorsementLocationWithEffectiveDate = async (
    schema: string,
    companyId: string,
    stateAbbr: string,
    locationUniqueID: string | undefined | null,
    effectiveDate: Date | null | undefined,
  ): Promise<string> => {
    let msg = "";

    // ValidateLocationEffectiveDate returns 400 unless CompanyID, State, LocationUniqueID and
    // DefaultDate are all present, so only call it once every param is known.
    if (!isReadOnly && companyId && stateAbbr && locationUniqueID && isValidDate(effectiveDate)) {
      try {
        const { data } = await axiosSecuredInstance.get<boolean>(
          `/Company/ValidateLocationEffectiveDate`,
          {
            params: {
              CompanyID: companyId,
              State: stateAbbr,
              LocationUniqueID: locationUniqueID,
              DefaultDate: formatDateToString(effectiveDate),
            },
          }
        );

        if (data !== true) {
          msg = UIConstants.LOCATION_NOT_VALID_FOR_SELECTED_EFFECTIVE_DATE;
        }
      }
      catch (error: any) {
        console.error("Error validating the agency location effective date", error);
        // Do not block the submit on a transport failure -- the location was picked from a list
        // that was itself filtered by effective date.
      }
    }

    // Always write, even when the rule could not run: resetValidationRequiredFlags only clears
    // the isValidationRequired flags, so a message left over from a previous submit would
    // otherwise persist.
    setValue(`${schema}.locationValidationMsg`, msg);
    return msg;
  }

  const validateLocationWithEffectiveDate = async (
    product: string,
    index: number,
    agencyId: string,
    stateAbbr: string,
    effectiveDate: Date | undefined | null,
    locationDisplayName: string | undefined | null,
  ): Promise<string> => {
    let msg = "";

    try {
      if (agencyId && stateAbbr && isValidDate(effectiveDate) && locationDisplayName) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        // Cancel previous request for this component instance only
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const stringDefaultDate = formatDateToString(effectiveDate);
        const { data } = await axiosSecuredInstance.get(
          `/Company/GetLocationsByCompanyState`,
          {
            params: {
              CompanyID: agencyId,
              State: stateAbbr,
              DefaultDate: stringDefaultDate,
            },
            signal: controller.signal,
          }
        );

        const matchedLocation = data?.find((location: any) => location.locationDisplayName === locationDisplayName);
        if (!matchedLocation) {
          msg = "Invalid Agency Location";
        }
      }
    }
    catch (error: any) {
      if (error?.name !== "AbortError" && error?.name !== "CanceledError") {
        console.error("Error fetching agency locations", error);
        // No validation error is required here, as users can only select valid locations during data entry.
      }
    }

    // set agency location validation message
    setValue(`${product}.${index}.locationValidationMsg`, msg);
    return msg;
  }

  return {
    validateEffectiveDate,
    validateEndorsementWithEffectiveDate,
    validateEndorsementLocationWithEffectiveDate,
    validateLocationWithEffectiveDate
   };
};

export default useEffectiveDateValidation;
